import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Games as GamesIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, getDocs, doc, getDoc } from 'firebase/firestore'
import toast from 'react-hot-toast'
import WordMatch from './WordMatch'

const WordMatchLevelSelection = ({ userData, userLevel, onBack }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [currentUserLevel, setCurrentUserLevel] = useState(userLevel || 'A1')
  const [currentPage, setCurrentPage] = useState('word-match-level-selection')
  const [selectedLevel, setSelectedLevel] = useState(null)
  
  console.log('WordMatchLevelSelection props:', { userData, userLevel, onBack })
  const [totalWords, setTotalWords] = useState(0)
  const [availableLevels, setAvailableLevels] = useState([])
  const [completedLevels, setCompletedLevels] = useState([])

  useEffect(() => {
    if (user && userData) {
      const level = userLevel || userData.level || 'A1'
      setCurrentUserLevel(level)
      loadCompletedLevels(level)
    }
  }, [user, userData, userLevel])

  useEffect(() => {
    if (user && userData && currentUserLevel) {
      loadLevels(currentUserLevel)
    }
  }, [user, userData, currentUserLevel, completedLevels])

  const loadCompletedLevels = async (level) => {
    try {
      console.log('Loading completed levels for user:', user.uid, 'level:', level)
      
      // Uygulama ile aynı yapıyı kullan: /gameProgress/A1/wordMatch/{userId}/completedLevels
      const firestoreLevel = level.toUpperCase()
      const completedLevelsRef = collection(db, 'gameProgress', firestoreLevel, 'wordMatch', user.uid, 'completedLevels')
      const completedLevelsSnapshot = await getDocs(completedLevelsRef)
      
      const completedLevels = []
      completedLevelsSnapshot.forEach(doc => {
        const levelNumber = parseInt(doc.id.replace('level_', ''))
        if (!isNaN(levelNumber)) {
          completedLevels.push(levelNumber)
        }
      })
      
      setCompletedLevels(completedLevels)
      console.log('Completed levels from gameProgress collection:', completedLevels)
    } catch (error) {
      console.error('Error loading completed levels:', error)
      setCompletedLevels([])
    }
  }

  const loadLevels = async (level) => {
    try {
      setLoading(true)
      console.log('Loading levels for:', level)

      // Android uygulamasındaki aynı yapıyı kullan
      const firestoreLevel = level.toUpperCase()
      const fieldName = firestoreLevel === 'EXAM' ? 'wordpairs' : 'wordPairs'

      const levelsRef = collection(db, 'words', firestoreLevel, 'levels')
      const levelsSnapshot = await getDocs(levelsRef)

      console.log('Found', levelsSnapshot.size, 'level documents')

      const levels = []

      // Her level dokümanını kontrol et
      levelsSnapshot.forEach(doc => {
        const levelNumber = parseInt(doc.id)
        const data = doc.data()
        const levelWords = data[fieldName] || []

        console.log(`Level ${levelNumber}: ${levelWords.length} words`)

        if (levelWords.length > 0) {
          // Level'ın açık olup olmadığını kontrol et
          const isCompleted = completedLevels.includes(levelNumber)
          const isNextLevel = levelNumber === 1 || completedLevels.includes(levelNumber - 1)
          const isAvailable = isCompleted || isNextLevel

          console.log(`Level ${levelNumber}: completed=${isCompleted}, available=${isAvailable}, wordCount=${levelWords.length}`)

          levels.push({
            level: levelNumber,
            wordCount: levelWords.length,
            available: isAvailable,
            completed: isCompleted
          })
        }
      })

      // Level\'ları numaraya göre sırala
      levels.sort((a, b) => a.level - b.level)

      console.log('Available levels:', levels.length)
      setTotalWords(levels.reduce((sum, level) => sum + level.wordCount, 0))

      setAvailableLevels(levels)
    } catch (error) {
      console.error('Error loading levels:', error)
      toast.error('Level\'lar yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleLevelSelect = (level) => {
    if (!level.available) {
      toast.error('Bu level henüz mevcut değil')
      return
    }
    
    console.log('WordMatchLevelSelection handleLevelSelect:', { level, currentUserLevel })
    
    // Word Match oyununa yönlendir ve seçilen level'ı gönder
    setCurrentPage('word-match')
    setSelectedLevel(level.level)
  }

  const handleBackToWordMatchLevelSelection = (data) => {
    if (data && data.page) {
      setCurrentPage(data.page)
    } else {
      setCurrentPage('word-match-level-selection')
    }
  }

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => {
                console.log('WordMatchLevelSelection back button clicked, onBack:', onBack)
                if (onBack && typeof onBack === 'function') {
                  onBack()
                } else {
                  console.error('onBack is not a function:', onBack)
                }
              }}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Level Seçimi
            </Typography>
          </Toolbar>
        </AppBar>
        <Container maxWidth="md" sx={{ py: 3 }}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Yükleniyor...</Typography>
          </Box>
        </Container>
      </Box>
    )
  }

  // Word Match oyununu göster
  if (currentPage === 'word-match' && selectedLevel) {
    return <WordMatch 
      userData={userData} 
      onBack={handleBackToWordMatchLevelSelection}
      selectedLevel={selectedLevel}
    />
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => {
              console.log('WordMatchLevelSelection main back button clicked, onBack:', onBack)
              if (onBack && typeof onBack === 'function') {
                onBack()
              } else {
                console.error('onBack is not a function:', onBack)
              }
            }}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Word Match - Level Seçimi
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            Seviye: {currentUserLevel}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3, pb: 10 }}>
        {/* Header Card */}
        <Card sx={{ 
          mx: 2, 
          mt: 2, 
          borderRadius: 3,
          boxShadow: 4,
          backgroundColor: '#ffffff',
          border: '2px solid #1976d2'
        }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <GamesIcon sx={{ fontSize: 56, color: '#1976d2', mb: 3 }} />
            <Typography variant="h3" sx={{ 
              color: '#1976d2', 
              fontWeight: 'bold',
              mb: 1
            }}>
              Word Match
            </Typography>
            <Typography variant="h6" sx={{ color: '#1976d2', opacity: 0.8, mb: 2 }}>
              Level Seçimi
            </Typography>
            <Typography variant="body1" sx={{ color: '#1976d2' }}>
              Toplam {totalWords} kelime • {availableLevels.length} level
            </Typography>
          </CardContent>
        </Card>

        {/* Levels Grid */}
        <Grid container spacing={3} sx={{ mt: 2, px: 2 }}>
          {availableLevels.map((level) => (
            <Grid item xs={6} sm={4} md={3} key={level.level}>
                               <Card 
                   sx={{ 
                     height: '100%',
                     borderRadius: 3,
                     boxShadow: 4,
                     background: level.completed 
                       ? 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)'
                       : level.available 
                       ? 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                       : 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
                     border: level.completed 
                       ? '2px solid #4caf50'
                       : level.available 
                       ? '2px solid #e0e0e0'
                       : '2px solid #ccc',
                     cursor: level.available ? 'pointer' : 'not-allowed',
                     transition: 'all 0.3s ease',
                     opacity: level.available ? 1 : 0.6,
                     '&:hover': level.available ? {
                       transform: 'translateY(-4px)',
                       boxShadow: 8,
                       borderColor: level.completed ? '#4caf50' : '#1976d2'
                     } : {}
                   }}
                   onClick={() => handleLevelSelect(level)}
                 >
                <CardContent sx={{ 
                  p: 3, 
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                                     <Typography variant="h2" sx={{ 
                     fontWeight: 'bold',
                     color: level.completed ? 'white' : level.available ? '#1976d2' : '#999',
                     mb: 1
                   }}>
                     {level.level}
                   </Typography>
                   <Typography variant="body2" sx={{ 
                     color: level.completed ? 'rgba(255,255,255,0.8)' : level.available ? 'text.secondary' : '#999',
                     mb: 1
                   }}>
                     {level.wordCount} kelime
                   </Typography>
                   <Typography variant="caption" sx={{ 
                     color: level.completed ? 'white' : level.available ? '#4caf50' : '#999',
                     fontWeight: 'bold'
                   }}>
                     {level.completed ? 'Tamamlandı' : level.available ? 'Mevcut' : 'Kilitli'}
                   </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Info Alert */}
        <Alert severity="info" sx={{ 
          mt: 4, 
          mx: 2, 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
          border: '2px solid #1976d2'
        }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
            🎮 Her level 10 kelime içerir. Level 1.1 ve 1.2 olmak üzere 2 aşamadan oluşur.
            Tüm kelimeleri eşleştirerek level'ı tamamlayın!
          </Typography>
        </Alert>
      </Container>
    </Box>
  )
}

export default WordMatchLevelSelection
